import { WIZ_UPLOAD_ACCEPT_FORMAT, WIZ_UPLOAD_ERROR, WizFileUtils, WizMathUtils } from '@wizeoo/akm-core-lib';
import { WizRoot } from '../../../components/views/wiz-root';
import { ElementRef } from '@angular/core';
import { IWIZComponent } from '../../../interfaces/iwiz-component';
import { Attachment } from '@wizeoo/akm-message-lib';
import { Observable } from 'rxjs';

// constantes définissant les formats de fichiers éligibles
export enum BATCH_FILE_FORMAT {
  CSV = 'csv',
  JSON = 'json',
  ZIP = "zip"
}

/**
 * paramètre pour chargement d'une image
 */
export interface ImageUploadParameter{
  component: IWIZComponent,
  elementRef: ElementRef,
  dom?: string, 
  bu?: string,
  vision?: string,
  viewCallback?: Function // éventuelle callback à jouer sur retour du batchEngine (retour du retour)
  environment?: boolean; // mode capture pour lancer la caméra sur mobile
  multiple?: boolean, // false par défaut
  thumb?: boolean // générer le thumb
}

/**
 * paramètre pour chargement d'un fichier local
 */
export interface UploadParameter{
  callback: Function, // traitement sur retour d'upload
  accept: WIZ_UPLOAD_ACCEPT_FORMAT[],
  multiple: boolean, // false par défaut
  maxSize?: number, 
  elementRef?: ElementRef,
  component?: IWIZComponent,
  targetPath?: string,
  files?: File[], // fichiers en retour
  contents?: string[], // contenus
  errors?: string[], // erreurs
  viewCallback?: Function // éventuelle callback à jouer sur retour du batchEngine (retour du retour)
  dom?: string, 
  bu?: string,
  vision?: string,
  noEncode?: boolean // pas d'encodage en base64 du fichier
  medias?: Attachment[]; // résultats sur chargement multiple
  environment?: boolean; // mode capture pour lancer la caméra sur mobile
  thumb?: boolean // générer le thumb
}


/**
 * WizUploadManager
 * file manager
 * permet d'uploader des fichiers
 */
export class WizUploadManager {

  /** les paramètres d'appel */
  private static parameters: UploadParameter;

  /** max size en Mo */
  private static maxSize: number;

  /** taille des images max en pixel */
  private static maxImageSize: number = 1000;


  /**
   * chargement de fichiers depuis le fileSystem
   * Par défaut, image (Jpg/png)
   * @param callback méthode de retour dans la vue, retourne le base64 / string
   * @param accept[]: format autorisés
   * @param multiple boolean
   * @param maxSize: nbr de mégas autorisés (défaut 1Mo)
   */
  public static loadFile(parameters: UploadParameter) {

    this.parameters = parameters;

    this.parameters.errors = [];
    this.parameters.files = [];
    this.parameters.contents = [];

    const uploadComp = document.createElement('input');

    if (!this.parameters.accept) { // chargement d'image par défaut
      this.parameters.accept = [
        WIZ_UPLOAD_ACCEPT_FORMAT.IMAGE_JPEG, 
        WIZ_UPLOAD_ACCEPT_FORMAT.IMAGE_PNG, 
        WIZ_UPLOAD_ACCEPT_FORMAT.IMAGE_WEBP,
        WIZ_UPLOAD_ACCEPT_FORMAT.IMAGE_AVIF
      ];
      uploadComp.setAttribute('accept', 'image/*');

      if(this.parameters.environment === true) {
        uploadComp.setAttribute('capture', 'environment');
      }
      
    } else {
      // set accepted files
      uploadComp.setAttribute('accept', this.parameters.accept.join());
    }

    if (this.parameters.maxSize) {
      this.maxSize = this.parameters.maxSize * 1000 * 1024;
    } else {
      this.maxSize = 20 * 1000 * 1024; // 10 Mo
    }

    // Création d'un input element pour ouvrir l'explorateur de fichier
    // Le seul moyen d'ouvrir le filesystem est de déclencher un clic sur un <input type=file>
    // On viens donc en créer un dynamiquement sur le wizRoot et le supprimer directement
    
    uploadComp.setAttribute('type', 'file');
   
    // prise de la cam sur mobile
    // uploadComp.setAttribute('capture', 'environment');

    // Ajout d'un uid pour qu'il soit unique
    uploadComp.setAttribute('id', WizMathUtils.createUUID());


    if (this.parameters.multiple) {
      uploadComp.setAttribute('multiple', 'multiple');
    }

    // ajout de l'input au type
    WizRoot.root().nativeElement.appendChild(uploadComp);

    // l'input et non visible pour l'utilisateur
    uploadComp.style.visibility = 'hidden';

    // simule le click de l'utilisateur
    uploadComp.click();
    uploadComp.addEventListener('change', this.handleFiles.bind(this), false);

    // suppression de l'élement créer
    WizRoot.root().nativeElement.removeChild(uploadComp);

  }

  /**
   * Callback déclenchée lorsque qu'un ou plusieurs fichiers
   * sont séléctionnées via le file system
   * @param event: Event
   */
  private static handleFiles(event) {
    const files: FileList = event.currentTarget.files;



    // vérification de la size et de l'extension
    Array.from(files).forEach((file: File) => {
      if (file.size > this.maxSize) {
        this.parameters.errors.push(WIZ_UPLOAD_ERROR.UPLOAD_ERROR_MAX_SIZE);
      } else if (WizUploadManager.isValidFormat(file, event.currentTarget.accept) === false) {
        this.parameters.errors.push(WIZ_UPLOAD_ERROR.UPLOAD_ERROR_FILE_EXTENSION);
      } 

      this.parameters.files.push(file);  
    });

    if(this.parameters.errors.length > 0) { // une erreur
      this.parameters.callback(this.parameters);
    } else {
      
      // // TODO ici resize si image
      // console.log('handleFiles', this.parameters);
      
      // quand tous les fichiers sont OK, on appelle la callback
      this.convertFileListToBase64(this.parameters.files.slice(0,10)).then(any => {
        this.parameters.callback(this.parameters);
      });
    }

  }

  /**
   * convertPromiseFileToBase64
   * Promise permettant de faire la convertion d'un file en une base64
   * La base64 est du texte qu'on peux mettre dans source des balises img
   * @param file: File. File a convertir
   * @return Promise<string | ArrayBuffer>
   */
  public static convertFileToBase64Promise(file: File): Promise<string | ArrayBuffer> {
       
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        let resAr: string[] = (event.target.result as string).split(',');
        
        if(!file.type.startsWith('image')) { // médias hors image, on retourne le résultat
          this.parameters.contents.push(resAr[1]);
          resolve(reader.result);
        } else { // gestion des images, taille max 1000x1000

          const img = new Image();
          img.onload = (event: any) => { 
            const { width, height } = this.getScaledSize(img, this.maxImageSize);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;

            // Limites pour Safari / iOS — éviter crashs
            const MAX_CANVAS = 4096;
            canvas.width = Math.min(width, MAX_CANVAS);
            canvas.height = Math.min(height, MAX_CANVAS);

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // let b64sized = canvas.toDataURL(file.type, 0.92);
            let b64sized =canvas.toDataURL('image/webp', 0.8)
            resAr = (b64sized as string).split(',');
            this.parameters.contents.push(resAr[1]);
            // console.log('image sized', file, canvas.width);
            resolve(b64sized);

          };

          img.src = event.target!.result as string;
        }
      };
      reader.onerror = error => reject(error);
    });
  }


  /**
   * Calcule la taille réduite proportionnellement
   */
  public static getScaledSize(img: HTMLImageElement, max: number) {
    let { width, height } = img;

    if (width > height) {
      if (width > max) {
        height = height * (max / width);
        width = max;
      }
    } else {
      if (height > max) {
        width = width * (max / height);
        height = max;
      }
    }
    return { width, height };
  }


  /**
   * Convertion de FileList en base 64
   * @param fileList: fileList  liste à convertir
   * @return un PromiseAll
   */
  public static convertFileListToBase64(files: File[]): Promise<Promise<string | ArrayBuffer>[]> {
    const promises = [];
    // Utilisation d'un array from car le prototype de FileList ne possède pas de forEach
    Array.from(files).forEach((file: File) => {
      promises.push(WizUploadManager.convertFileToBase64Promise(file));
    });

    // Execute la convertion sur tout les files et retourne le resultat
    return Promise.all(promises);
  }

  /**
   * Vérification du format
   * @param file: File
   * @param accept liste des format renvoyer par l'input
   * @return: return true if valid format
   */
  private static isValidFormat(file: File, accept: string): boolean {
    const acceptedFormat: string[] = accept.split(','); // split string on comma space

    if (accept.length === 0) {
      return null;
    }

    let hasWildCard: boolean = false;
    acceptedFormat.forEach(s => {
      if(s === WIZ_UPLOAD_ACCEPT_FORMAT.FILE_ANY) {
        hasWildCard = true;
      }
    })
    if(hasWildCard) {
      return true;
    }

    let hasWildImage: boolean = false;
    // gestion spécifique du image/*
    if(accept === 'image/*') {
      hasWildImage = true;
    }


    if (file instanceof File) {
      // gestion du image/*
      if(file.type.startsWith('image') && hasWildImage) {
        return true;
      }
      
      const ext = '.' + WizFileUtils.getExtension(file);
      if (acceptedFormat.indexOf(ext.toLowerCase()) === -1) {
        return false;
      }
    }
    return true;
  }







}
